import { supabase, handleSupabaseError } from './supabase';
import { User, Task, Message, Department } from '../../types';
import { logger } from '../../utils/logger';

/**
 * User-related queries
 */
export const userQueries = {
  async getCurrentUser() {
    try {
      const { data: session, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      if (!session.session) return null;

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.session.user.id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error('Failed to get current user:', error);
      return null;
    }
  },

  async getUsers() {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('name');

      if (error) throw error;
      return data;
    } catch (error) {
      return handleSupabaseError(error);
    }
  },

  async getUsersByDepartment(department: Department) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('department', department)
        .order('name');

      if (error) throw error;
      return data;
    } catch (error) {
      return handleSupabaseError(error);
    }
  },

  async getUserById(id: string) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      return handleSupabaseError(error);
    }
  }
};

/**
 * Task-related queries
 */
export const taskQueries = {
  async getTasks() {
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select(`
          *,
          subtasks:subtasks(*),
          comments:task_comments(
            *,
            user:profiles(name, avatar)
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      return handleSupabaseError(error);
    }
  },

  async getTaskById(id: string) {
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select(`
          *,
          subtasks:subtasks(*),
          comments:task_comments(
            *,
            user:profiles(name, avatar)
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      return handleSupabaseError(error);
    }
  },

  async getTasksByDepartment(department: Department) {
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select(`
          *,
          subtasks:subtasks(*),
          comments:task_comments(
            *,
            user:profiles(name, avatar)
          )
        `)
        .eq('department', department)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      return handleSupabaseError(error);
    }
  },

  async getTasksByAssignee(userId: string) {
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select(`
          *,
          subtasks:subtasks(*),
          comments:task_comments(
            *,
            user:profiles(name, avatar)
          )
        `)
        .eq('assigned_to', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      return handleSupabaseError(error);
    }
  }
};

/**
 * Message-related queries
 */
export const messageQueries = {
  async getMessages() {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select(`
          *,
          sender:profiles(*),
          acknowledgments:message_acknowledgments(*)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      return handleSupabaseError(error);
    }
  },

  async getMessagesByDepartment(department: Department) {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select(`
          *,
          sender:profiles(*),
          acknowledgments:message_acknowledgments(*)
        `)
        .contains('departments', [department])
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      return handleSupabaseError(error);
    }
  },

  async getDirectMessages(userId: string) {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select(`
          *,
          sender:profiles(*),
          acknowledgments:message_acknowledgments(*)
        `)
        .eq('is_direct', true)
        .or(`sender_id.eq.${userId},message_recipients.user_id.eq.${userId}`)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      return handleSupabaseError(error);
    }
  }
};